import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Plus, Edit, Trash2, Eye, Package, Share2 } from 'lucide-react';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';
import { WhatsAppShareDialog } from '@/components/share/WhatsAppShareDialog';
import { useWhatsAppShare } from '@/hooks/useWhatsAppShare';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  images: string[];
  category: string;
  stock: number;
  status: string;
  views: number;
  likes: number;
  created_at: string;
}

export default function ProductList() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [businessName, setBusinessName] = useState<string>('');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { share, isDialogOpen, setIsDialogOpen, shareData, createShareableLink } = useWhatsAppShare();

  useEffect(() => {
    if (user) {
      fetchProducts();
      checkSubscription();
      fetchBusinessName();
    }
  }, [user]);

  const fetchBusinessName = async () => {
    if (!user) return;
    
    const { data } = await supabase
      .from('profiles')
      .select('business_name')
      .eq('id', user.id)
      .single();
    
    if (data?.business_name) {
      setBusinessName(data.business_name);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (!error && data) {
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const fetchProducts = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }
      setProducts(data || []);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      setProducts(products.filter(p => p.id !== deleteId));
      toast({
        title: 'Success',
        description: 'Product deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    }
    setDeleteId(null);
  };

  const handleShareProduct = async (product: Product) => {
    const shareableLink = createShareableLink(product.id);
    
    await share({
      title: product.title,
      description: product.description,
      price: product.price,
      imageUrl: product.images?.[0],
      shareableLink,
      businessName: businessName,
    });
  };

  const shareAllProductsToWhatsApp = async () => {
    const activeProducts = products.filter(p => p.status === 'active');
    
    if (activeProducts.length === 0) {
      toast({
        title: 'No Active Products',
        description: 'You need active products to share',
        variant: 'destructive',
      });
      return;
    }

    try {
      let message = `🛍️ Check out my store catalog!\n\n`;
      
      activeProducts.forEach((product, index) => {
        const shareableLink = createShareableLink(product.id);
        message += `${index + 1}. ${product.title}\n`;
        message += `   💰 ₦${product.price.toLocaleString()}\n`;
        message += `   📊 ${product.stock} in stock\n`;
        message += `   🛒 ${shareableLink}\n\n`;
      });
      
      message += `📱 Click any link above to purchase directly!`;

      // Try Web Share API first (native sharing)
      if (navigator.share) {
        await navigator.share({
          title: `${businessName || 'SabiTradeNG'} - Product Catalog`,
          text: message,
        });
        toast({
          title: 'Catalog shared!',
          description: `Successfully shared ${activeProducts.length} products`,
        });
      } else {
        // Fallback to WhatsApp direct share
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        toast({
          title: 'Catalog ready!',
          description: `Choose who to share ${activeProducts.length} products with`,
        });
      }
    } catch (error) {
      console.error('Error sharing catalog:', error);
      if (error.name !== 'AbortError') {
        toast({
          title: 'Share failed',
          description: 'Failed to share catalog',
          variant: 'destructive',
        });
      }
    }
  };

  const canAddMoreProducts = () => {
    if (!subscription) return true;
    if (subscription.tier === 'premium') return true;
    return subscription.productCount < subscription.productLimit;
  };

  if (loading) {
    return <div className="animate-pulse">Loading products...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* WhatsApp Share Dialog */}
      {shareData && (
        <WhatsAppShareDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          shareData={shareData}
        />
      )}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Products</h1>
          {subscription && (
            <p className="text-muted-foreground mt-1">
              {subscription.productCount} / {subscription.productLimit || 'Unlimited'} products
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {products.length > 0 && (
            <Button 
              onClick={shareAllProductsToWhatsApp}
              variant="whatsapp"
            >
              <WhatsAppIcon className="h-4 w-4 mr-2" size={16} />
              Share Catalog
            </Button>
          )}
          <Button 
            onClick={() => navigate('/seller/add-product')}
            disabled={!canAddMoreProducts()}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {!canAddMoreProducts() && (
        <Card className="mb-6 bg-warning/10 border-warning">
          <CardHeader>
            <CardTitle>Product Limit Reached</CardTitle>
            <CardDescription>
              You've reached your product limit. Upgrade your subscription to add more products.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/seller/dashboard?tab=subscription')}>
              Upgrade Subscription
            </Button>
          </CardFooter>
        </Card>
      )}

      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No products yet</h3>
            <p className="text-muted-foreground mb-4">Start by adding your first product</p>
            <Button 
              onClick={() => navigate('/seller/add-product')}
              disabled={!canAddMoreProducts()}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <Card key={product.id}>
              {product.images && product.images[0] && (
                <div className="aspect-video bg-muted relative overflow-hidden">
                  <img 
                    src={product.images[0]} 
                    alt={product.title}
                    className="object-cover w-full h-full"
                  />
                  <Badge 
                    className="absolute top-2 right-2"
                    variant={product.status === 'active' ? 'default' : 'secondary'}
                  >
                    {product.status}
                  </Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="line-clamp-1">{product.title}</CardTitle>
                <CardDescription className="line-clamp-2">
                  {product.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold">
                    ₦{product.price.toLocaleString()}
                  </span>
                  <Badge variant="outline">{product.stock} in stock</Badge>
                </div>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {product.views}
                  </span>
                  <span className="flex items-center gap-1">
                    ❤️ {product.likes}
                  </span>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/seller/edit-product/${product.id}`)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="whatsapp"
                  size="sm"
                  onClick={() => handleShareProduct(product)}
                  title="Share Product"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteId(product.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Product Dialog - No longer needed with native sharing */}
    </div>
  );
}